import React, { useEffect, useState } from "react";

import { Controller, useForm } from "react-hook-form";
import { Customer } from "../../../../core";
import { number } from "currency-codes";
import MergePhone from "../merge-phone";
import MergeEmail from "../merge-email";
import { DropdownList } from "react-widgets";
import { format } from "date-fns";
import MergeField, { IMergeModel } from "../merge-field";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import api from "../../../../api";
import { toastr } from "react-redux-toastr";
import { ValidateAlphaNumeric, ValidateGST } from "../../../../utils/Validatation";

interface IMergeFormProps {
  designationId: string;
  gstNumber: string;
  panNumber: string;
  dinNumber: string;
  firstName: string;
  lastName: string;
  middleName: string;
  phones: Phone[];
  emails: Email[];
  dob: string;
  ageRange: string;
  gender: string;
  addresses: Address[];
}

interface Address {
  isPrimary: boolean;
  value: string;
  type: string;
}

interface Email {
  isPrimary: boolean;
  email: string;
  type: string;
}

interface Phone {
  isPrimary: boolean;
  phone: string;
  type: string;
}

interface MergeReviewCustomerProps {
  onReload?: () => void;
  onClose: () => void;
  onBack: () => void;
  className?: string;
  customer: Customer;
  mergingCustomers: Customer[];
}

const MergeReviewCustomer: React.FunctionComponent<MergeReviewCustomerProps> = ({
  onClose,
  onBack,
  className,
  customer,
  mergingCustomers,
}) => {
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const { control, handleSubmit, errors, watch, trigger } = useForm<any>({
    reValidateMode: "onChange",
    mode: "onChange",
  });
  const onSubmit = async (data: IMergeFormProps) => {
    const customerDataForMerge: Customer = {
      firstName: data?.firstName,
      lastName: data?.lastName,
      middleName: data?.middleName,
      dob: data?.dob,
      phones: data?.phones?.map((item) => {
        return {
          phone: item?.phone,
          isPrimary: item?.isPrimary,
          phoneType: item?.type,
        };
      }),
      emails: data?.emails?.map((item) => {
        return {
          email: item?.email,
          isPrimary: item?.isPrimary,
          emailType: item?.type,
        };
      }),
      addresses: data?.addresses?.map((item) => {
        return {
          address: item?.value,
          isPrimary: item?.isPrimary,
          addressType: item?.type,
        };
      }),
      ageRange: data?.ageRange,
      gender: data?.gender,
      dinNumber:data?.dinNumber,
      panNumber:data?.panNumber,
      gstNumber:data?.gstNumber,
      designationId:data?.designationId,
    };
    const mergingCustomerIds = mergingCustomers?.map((item) => {
      return item?._id ?? "";
    });
    try {
      const response = await api.customer.mergeCustomer(
        customer?._id!,
        mergingCustomerIds,
        customerDataForMerge
      );
      if (response?.status === 200) {
        toastr.success("Success", `Customers merged successfully !`);
        onClose?.();
      }
    } catch (error) {
      toastr.error(
        "Error",
        error.response.data?.message ?? `Some errors occured while merging`
      );
    }
  };

  const phoneContacts = [customer, ...mergingCustomers]
    ?.filter((item) => item?.phones !== undefined)
    ?.map((item) => {
      if (item?.phones && item?.phones?.length > 0) {
        return {
          name: item?.firstLastName,
          numbers:
            item?.phones?.map((ph) => {
              return {
                phone: ph.phone,
                phoneType: ph.phoneType,
                isPrimary: ph.isPrimary,
              };
            }) ?? [],
        };
      }
    })
    .filter(Boolean);
  const emailContacts = [customer, ...mergingCustomers]
    ?.filter((item) => item?.emails !== undefined)
    ?.map((item) => {
      if (item?.emails && item?.emails?.length > 0) {
        return {
          name: item?.firstLastName,
          emails:
            item?.emails?.map((ph) => {
              return {
                email: ph.email,
                emailType: ph.emailType,
                isPrimary: ph.isPrimary,
              };
            }) ?? [],
        };
      }
    })
    .filter(Boolean);

  const address = [customer, ...mergingCustomers]
    ?.filter((item) => item?.addresses !== undefined)
    ?.map((item) => {
      if (item?.addresses && item?.addresses?.length > 0) {
        return {
          title: item?.firstLastName,
          items:
            item?.addresses?.map((ph) => {
              return {
                value: ph.address ?? "",
                type: ph.addressType ?? "",
                isPrimary: ph.isPrimary ?? "",
              };
            }).filter(itm => itm.value) ?? [],
        };
      }
    })
    .filter((item)=>item?.items?.length);

  const dob = [customer, ...mergingCustomers]
    ?.filter((item) => item?.dob !== undefined)
    ?.map((item) => {
      if (item?.dob !== undefined) {
        return {
          name: item?.firstLastName ?? "",
          label: format(new Date(item?.dob), "dd/MM/yyyy"),
          value: item?.dob,
        };
      }
    })
    .filter(Boolean);
  const ageLimit = [customer, ...mergingCustomers]
    ?.map((item) => {
      if (item?.ageRange !== undefined) {
        return {
          name: item?.firstLastName,
          label: item?.ageRange,
          value: item?.ageRange,
        };
      }
    })
    .filter(Boolean);
  const gender = [customer, ...mergingCustomers]
    ?.map((item) => {
      if (item?.gender !== undefined) {
        return {
          name: item?.firstLastName,
          label: item?.gender,
          value: item?.gender,
        };
      }
    })
    .filter(Boolean);

  // const address = [
  //   {
  //     Address: "1234 Market Street, Suite 567 Metropolis, 560077 Bangalore",
  //     id: 30,
  //   },
  //   {
  //     Address: "5678 Elm Avenue, Apt 101 Metro City, 500067 Bangalore",
  //     id: 31,
  //   },
  //   {
  //     Address: "7890 Pine Drive, Floor 2 Metro City, 400089 Bangalore",
  //     id: 32,
  //   },
  // ];
  const renderDropdown = ({ item }: any) => (
    <>
      <div className="py-2">
        <div className="mb-1 text-dark font-weight-semibold">{item.label}</div>
        <span className="text-muted font-xxs font-italic d-block font-weight-normal">
          {item.name}
        </span>
      </div>
    </>
  );

  const renderCustomerMergeConfirmation = () => {
    return confirmation ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          style={{
            zIndex: 9999,
            background: "#00000047",
          }}
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                      Are you sure you want to merge the selected customers to the main customer ?
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={() => setConfirmation(false)}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        // onClick={() => onSubmit()}
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };
  return (
    <div className="modal-open customer-info-merge">
      <div
        className="detail-popup bg-white position-fixed top-0 w-100 left-0 h-100 overflow-auto in"
        style={{ zIndex: 9999 }}
      >
        <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-99">
          <div className="row align-items-center">
            <div
              className="col-auto"
              onClick={() => {
                onClose?.();
              }}
            >
              <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
            </div>
            <div className="col">
              <h5 className="mb-0 py-2">Review and Merge</h5>
            </div>
          </div>
        </div>
        <div className={`${className || ""} h-100`}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row h-100 mx-0">
              <div className="col-sm-4 col-12  border-right px-0 merge-lead-left-wrap customer-merge-left-wrap">
                <div className="border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                  <h5 className="font-normal font-weight-bold mb-0">
                    Customer Info
                  </h5>
                </div>

                <div className="px-4 pt-3" style={{ paddingBottom: "110px" }}>
                  {/* <form onSubmit={handleSubmit(onSubmit)}> */}
                  {/* Select Input */}
                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      First Name<span className="text-danger">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="firstName"
                      rules={{
                        required: {
                          value: true,
                          message: "First name is required",
                        },
                      }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          {...restProps}
                          data={[customer, ...mergingCustomers]
                            ?.filter(
                              (item) =>
                                item?.firstName !== "" &&
                                item?.firstName !== undefined
                            )
                            ?.map((item) => {
                              return {
                                label: item?.firstName,
                                value: item?.firstName,
                              };
                            })}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          // itemComponent={renderDropdown}

                          onChange={(val) => {
                            onChange(val?.value);
                          }}
                        />
                      )}
                      // render={(field) => (
                      //   <select className="form-control" {...field}>
                      //     {[customer, ...mergingCustomers].map((option) => (
                      //       <option
                      //         key={option.firstName}
                      //         value={option.firstName}
                      //       >
                      //         {option.firstName}
                      //       </option>
                      //     ))}
                      //   </select>
                      // )}
                    />
                    {errors?.firstName?.message && (
                      <ErrorMessage className="align-self-start">
                        {errors?.firstName?.message}
                      </ErrorMessage>
                    )}
                  </div>
                  {/* <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Middle Name
                    </label>
                    <Controller
                      control={control}
                      name="middleName"
                      // rules={{ required: true }}
                      // render={(field) => (
                      //   <select className="form-control" {...field}>
                      //     {[customer, ...mergingCustomers].map((option) => (
                      //       <option
                      //         key={option.middleName}
                      //         value={option.middleName}
                      //       >
                      //         {option.middleName}
                      //       </option>
                      //     ))}
                      //   </select>
                      // )}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          {...restProps}
                          data={[customer, ...mergingCustomers]
                            ?.filter(
                              (item) =>
                                item?.middleName !== "" &&
                                item?.middleName !== undefined
                            )
                            ?.map((item) => {
                              return {
                                label: item?.middleName,
                                value: item?.middleName,
                              };
                            })}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          // itemComponent={renderDropdown}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div> */}
                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Last Name
                    </label>
                    <Controller
                      control={control}
                      name="lastName"
                      // rules={{ required: true }}
                      // render={(field) => (df
                      //   <select className="form-control" {...field}>
                      //     {[customer, ...mergingCustomers].map((option) => (
                      //       <option
                      //         key={option.lastName}
                      //         value={option.lastName}
                      //       >
                      //         {option.lastName}
                      //       </option>
                      //     ))}
                      //   </select>
                      // )}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          {...restProps}
                          data={[customer, ...mergingCustomers]
                            ?.filter(
                              (item) =>
                                item?.lastName !== "" &&
                                item?.lastName !== undefined
                            )
                            ?.map((item) => {
                              return {
                                label: item?.lastName,
                                value: item?.lastName,
                              };
                            })}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          // itemComponent={renderDropdown}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                    {errors?.lastName?.message && (
                      <ErrorMessage className="align-self-start">
                        {errors?.lastName?.message}
                      </ErrorMessage>
                    )}
                  </div>
                  {phoneContacts.length ? <div className="customer-details-info my-4">
                    <h3 className="font-normal font-weight-semibold mb-2">
                      Mobile Number
                    </h3>
                    <Controller
                      control={control}
                      name="phones"
                      // rules={{ required: true }}
                      render={({ onChange, name, ...restProps }) => (
                        <MergePhone
                          name={name}
                          onUpdate={onChange}
                          contacts={phoneContacts}
                          {...restProps}
                        />
                      )}
                    />
                    {/* {contacts?.map((contact, contactIndex) => (
                      <div key={contactIndex} className="contact">
                        <div className="row">
                          <div className="col-sm-11 left-wrap">
                            <p className="contact-name font-italic">
                              {contact.name}
                            </p>
                            {contact.numbers.map((num, index) => {
                             
                              const radioId = `radio_${contactIndex}_${index}`;
                              const checkboxId = `checkbox_${contactIndex}_${index}`;
                              return (
                                <div key={index} className="d-flex mb-1">
                                  <div className="contact-details w-100">
                                    <span className="number font-sm font-weight-semibold d-flex align-items-center">
                                      {num.number}
                                    </span>
                                    <span className="type font-sm border-left font-weight-semibold d-flex align-items-center">
                                      {num.type}
                                    </span>
                                  </div>

                                  <div className="radio-button d-flex justify-content-between align-items-center">
                              
                                    <div className="checkbox-box border">
                                      <input
                                        id={radioId}
                                        type="radio"
                                        name="mobileNumber"
                                      />
                                      <label
                                        htmlFor={radioId}
                                        className="pl-0 w-100 h-100 justify-content-center"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="14"
                                          height="10"
                                          viewBox="0 0 14 10"
                                          fill="none"
                                        >
                                          <path
                                            d="M10.9306 0.382293L4.86025 6.45808L2.24072 3.88229C1.72802 3.3696 0.897593 3.3696 0.384624 3.88229C-0.128345 4.39499 -0.128071 5.22542 0.384624 5.73839L3.88462 9.23839C4.18759 9.4987 4.52119 9.62448 4.86025 9.62448C5.19931 9.62448 5.53181 9.49629 5.7883 9.23997L12.7883 2.23997C13.301 1.72728 13.301 0.896848 12.7883 0.383879C12.2756 -0.12909 11.4446 -0.126301 10.9306 0.382293Z"
                                            fill="#9CA3AF"
                                          />
                                        </svg>
                                      </label>
                                    </div>

                                    <div className="ml-2">
                                      <input
                                        type="checkbox"
                                        id={checkboxId}
                                        name="additionalInfo"
                                      />
                                      <label
                                        className="pl-0 ml-1"
                                        htmlFor={checkboxId}
                                      >
                                        &nbsp;
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))} */}
                  </div> : null}

                  {emailContacts.length ? <div className="customer-details-info my-4">
                    <h3 className="font-normal font-weight-semibold mb-2">
                      Email
                    </h3>
                    <Controller
                      control={control}
                      name="emails"
                      // rules={{ required: true }}
                      render={({ onChange, name, ...restProps }) => (
                        <MergeEmail
                          name={name}
                          onUpdate={onChange}
                          contacts={emailContacts}
                          {...restProps}
                        />
                      )}
                    />
                  </div> :null}
                  {/* <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Date of birth
                    </label>
                    <Controller
                      control={control}
                      name="dob"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={dob}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div> */}

                  {/* <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Age limit
                    </label>
                    <Controller
                      control={control}
                      name="ageRange"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={ageLimit}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div> */}

                  {/* <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Gender
                    </label>
                    <Controller
                      control={control}
                      name="gender"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={gender}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div> */}
                   <div className={'customer-details-info my-4'}>
                      <h3 className="font-normal font-weight-semibold mb-2">
                        DIN Number
                      </h3>
                      <Controller
                        control={control}
                        name="dinNumber"
                        rules={{
                          validate: (value) => {
                            return !ValidateAlphaNumeric(value) ? "Invalid DIN Number" : undefined;
                          },
                          maxLength: 8,
                          minLength: 8,
                        }}
                        render={({ onChange, ...restProps}) => (
                          <DropdownList
                            {...restProps}
                            data={[customer, ...mergingCustomers]
                              ?.filter(
                                (item) =>
                                  item?.dinNumber !== "" &&
                                  item?.dinNumber !== undefined
                              )
                              ?.map((item) => {
                                return {
                                  label: item?.dinNumber,
                                  value: item?.dinNumber,
                                  name: item?.dinNumber,
                                };
                              })}
                            textField={"label"}
                            valueField={"value"}
                            placeholder={"Select"}
                            // itemComponent={renderDropdown}
                            onChange={(val) => {
                              onChange(val.value);
                            }}
                          />
                        )}
                      />
                      {errors.dinNumber && errors.dinNumber.type === "maxLength" && (
                        <ErrorMessage>DIN Number cannot have more than 8 characters!</ErrorMessage>
                      )}
                      {errors.dinNumber && errors.dinNumber.type === "minLength" && (
                        <ErrorMessage>DIN Number cannot have less than 8 characters!</ErrorMessage>
                      )}
                      {errors.dinNumber && errors.dinNumber.type === "validate" && (
                        <ErrorMessage>Invalid DIN Number</ErrorMessage>
                      )}
                    </div>
                    <div className={`form-group font-weight-semibold mb-4`}>
                      <label htmlFor="" className="font-sm text-uppercase">
                        PAN Number
                      </label>

                      <Controller
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (value) {
                              const panRegex = /[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                              return panRegex.test(value);
                            }
                          },
                          // minLength: 10,
                          maxLength: 10,
                        }}
                        name="panNumber"
                        render={({ onChange, ...restProps }) => (
                          <DropdownList
                            {...restProps}
                            data={[customer, ...mergingCustomers]
                              ?.filter(
                                (item) =>
                                  item?.panNumber !== "" &&
                                  item?.panNumber !== undefined
                              )
                              ?.map((item) => {
                                return {
                                  label: item?.panNumber,
                                  value: item?.panNumber,
                                };
                              })}
                            textField={"label"}
                            valueField={"value"}
                            placeholder={"Select"}
                            // itemComponent={renderDropdown}
                            onChange={(val) => {
                              onChange(val.value);
                            }}
                          />
                        )}
                      />

                      {errors.panNumber && errors.panNumber.type === "maxLength" && (
                        <ErrorMessage>PAN No cannot have more than 10 characters!</ErrorMessage>
                      )}
                      {errors.panNumber && errors.panNumber && (
                        <ErrorMessage>Invalid PAN No</ErrorMessage>
                      )}
                    </div>
                    <div className={`form-group font-weight-semibold mb-4`}>
                      <label htmlFor="" className="font-sm text-uppercase">
                        GST Number
                      </label>

                      <Controller
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (value && !ValidateGST(value)) {
                              return "Invalid GST";
                            }
                          },
                        }}
                        name="gstNumber"
                        render={({ onChange, ...restProps }) => (
                          <DropdownList
                            {...restProps}
                            data={[customer, ...mergingCustomers]
                              ?.filter(
                                (item) =>
                                  item?.gstNumber !== "" &&
                                  item?.gstNumber !== undefined
                              )
                              ?.map((item) => {
                                return {
                                  label: item?.gstNumber,
                                  value: item?.gstNumber,
                                };
                              })}
                            textField={"label"}
                            valueField={"value"}
                            placeholder={"Select"}
                            // itemComponent={renderDropdown}
                            onChange={(val) => {
                              onChange(val.value);
                            }}
                          />
                        )}
                      />
                      {errors.gstNumber?.message && (
                        <ErrorMessage>{errors.gstNumber?.message}</ErrorMessage>
                      )}
                    </div>
                    <div className={`form-group font-weight-semibold mb-4`}>
                      <label htmlFor="" className="font-sm text-uppercase">
                        Designation
                      {/* <span className="text-danger">*</span> */}
                      </label>
                      <Controller
                        control={control}
                        // rules={{ required: true }}
                        // rules={{ required: props?.isContactlessCustomer }}
                        name="designationId"
                        // defaultValue={props?.customerDetails?.designation?._id}
                        // defaultValue={
                        //   props?.isContactlessCustomer
                        //     ? props.designationId
                        //     : props?.customerDetails?.designation?._id
                        // }
                        render={({ onChange, ...restProps }) => (
                          <DropdownList
                            {...restProps}
                            data={[customer, ...mergingCustomers]
                              ?.filter(
                                (item) =>
                                  item.designation?._id !== "" &&
                                  item.designation?._id !== undefined
                              )
                              ?.map((item) => {
                                return {
                                  label: item.designation?.name,
                                  value: item.designation?._id,
                                };
                              })}
                            textField={"label"}
                            valueField={"value"}
                            placeholder={"Select"}
                            // itemComponent={renderDropdown}
                            onChange={(val) => {
                              onChange(val.value);
                            }}
                          />
                        )}
                      />
                      {errors.designationId && <ErrorMessage>Designation is required</ErrorMessage>}
                    </div>

                  {/* </form> */}
                </div>
              </div>
              <div className="col-sm-4 col-12 px-0 merge-lead-right-wrap  border-right">
                <div className="border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="font-normal font-weight-bold mb-0">
                      Addresses ({address?.length})
                    </h5>
                  </div>
                </div>
                <div className="px-4 pt-3" style={{ paddingBottom: "110px" }}>
                  <div className="customer-details-info mb-4 border-bottom pb-2">
                    <Controller
                      control={control}
                      name="addresses"
                      // rules={{ required: true }}
                      render={({ onChange, name, ...restProps }) => (
                        <MergeField
                          name={name}
                          onUpdate={onChange}
                          data={address}
                          {...restProps}
                          showType={true}
                        />
                      )}
                    />
                  </div>
                </div>
                {/* <div className="px-4 pt-3" style={{ paddingBottom: "110px" }}>
                  <div className="customer-details-info mb-4 border-bottom pb-2">
                    <h3 className="font-normal font-weight-semibold mb-2 pb-1">
                      Muhammad Nasar
                    </h3>
                    {address.map((addr, index) => {
                      // Generate unique id for each radio button and checkbox

                      return (
                        <div key={index} className="contact mb-2">
                          <div className="row">
                            <div className="col-sm-11 left-wrap">
                              <div className="d-flex">
                                <div
                                  className="contact-details w-100"
                                  style={{ height: "auto" }}
                                >
                                  <span className="number font-sm font-weight-semibold d-flex align-items-center">
                                    {addr.Address}
                                  </span>
                                </div>

                                <div
                                  className="radio-button d-flex justify-content-between align-items-center"
                                  style={{ height: "100%" }}
                                >
                                
                                  <div
                                    className="checkbox-box border"
                                    style={{ height: "100%" }}
                                  >
                                    <input
                                      id={`radio_${addr?.id?.toString()}`}
                                      type="radio"
                                      name="address"
                                    />
                                    <label
                                      htmlFor={`radio_${addr?.id?.toString()}`}
                                      className="pl-0 w-100 h-100 justify-content-center"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="10"
                                        viewBox="0 0 14 10"
                                        fill="none"
                                      >
                                        <path
                                          d="M10.9306 0.382293L4.86025 6.45808L2.24072 3.88229C1.72802 3.3696 0.897593 3.3696 0.384624 3.88229C-0.128345 4.39499 -0.128071 5.22542 0.384624 5.73839L3.88462 9.23839C4.18759 9.4987 4.52119 9.62448 4.86025 9.62448C5.19931 9.62448 5.53181 9.49629 5.7883 9.23997L12.7883 2.23997C13.301 1.72728 13.301 0.896848 12.7883 0.383879C12.2756 -0.12909 11.4446 -0.126301 10.9306 0.382293Z"
                                          fill="#9CA3AF"
                                        />
                                      </svg>
                                    </label>
                                  </div>

                                
                                  <div className="ml-2">
                                    <input
                                      type="checkbox"
                                      id={`checkbox_${addr?.id?.toString()}`}
                                      name={`additionalInfo_${index}`}
                                    />
                                    <label
                                      className="pl-0 ml-1"
                                      htmlFor={`checkbox_${addr?.id?.toString()}`}
                                    >
                                      &nbsp;
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div> */}
              </div>
              <div className="button-bottom-merge d-flex justify-content-end left-0 right-0">
                <button className="btn btn-outline-dark px-5" type="button" onClick={()=> onBack?.()}>Back</button>

                <button type="button" className="btn btn-primary px-4" onClick={()=>{
                  trigger().then((hasNoError) => {
                    if(hasNoError){
                      if(watch('phones')?.length || watch('emails')?.length){
                        setConfirmation(true)
                      }else{
                        toastr.error('Error', 'Please Select At least One Email or Phone Number')
                      }
                      
                    }
                  })
                  }}>
                  Review & Confirm
                </button>
              </div>
              {/* </form> */}
            </div>
            {renderCustomerMergeConfirmation()}
          </form>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </div>
  );
};

export default MergeReviewCustomer;
